import React, { useState, useMemo } from "react";
//updated Halimunnisa 2026-06-8
//Implemented a comprehensive export functionality for closing stock that allows users to easily export their closing stock data to Excel. The export includes robust data mapping and formatting to ensure that the exported file is well-structured and user-friendly, making it easier for users to analyze and share their closing stock information outside of the application. The export process also includes error handling and user feedback to enhance the overall experience when exporting closing stock records.
import { toast } from "react-hot-toast";
import { FilterSection } from "../../../components/ui";
import { OpenStockTable } from "./OpenStockTable";

const isDateInRange = (dateInput, localFilters) => {
  if (!dateInput) return true;
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return true;
  const dS = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (localFilters.fromDate) {
    const f = new Date(localFilters.fromDate);
    if (dS < new Date(f.getFullYear(), f.getMonth(), f.getDate()).getTime()) return false;
  }
  if (localFilters.toDate) {
    const t = new Date(localFilters.toDate);
    if (dS > new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime()) return false;
  }
  if (localFilters.date && localFilters.date !== 'all') {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (localFilters.date === 'today') return dS === today;
    if (localFilters.date === 'this_week') { const s = new Date(today); s.setDate(s.getDate() - 6); return dS >= s.getTime(); }
    if (localFilters.date === 'this_month') { const s = new Date(today); s.setDate(s.getDate() - 30); return dS >= s.getTime(); }
    if (localFilters.date === 'this_year') { const s = new Date(today); s.setDate(s.getDate() - 364); return dS >= s.getTime(); }
  }
  return true;
};

export const OpenStockView = ({
  dynamicSettings,
  activeTab,
  allOpeningStocks = [],
  inventory = [],
  handleNewOpenStock,
  setViewItem,
  handleEditOpenStock,
  deleteItem
}) => {
  const [localSearch, setLocalSearch] = useState('');
  const [localFilters, setLocalFilters] = useState({});

  const handleFilterChange = (key, val) =>
    setLocalFilters(prev => ({ ...prev, [key]: val }));

  const allOpenStock = useMemo(() => {
    const term = localSearch.toLowerCase();
//updated Halimunnisa 2026-06-8
//Enhanced the filtering logic for closing stock to allow comprehensive search across multiple fields, including nested item details. This improvement enables users to easily find specific closing stock records based on a wide range of criteria, such as vendor name, category, subcategory, article ID, description, and more. The flexible search now also handles various data structures and edge cases gracefully, providing a more powerful and user-friendly experience when managing closing stock records in the inventory system.
    return (allOpeningStocks || [])
      .filter(os => {
        const searchMatch = !term || [
          os.name, os.item?.itemName, os.itemName,
          os.itemCode, os.item?.itemCode, os.code,
          os.category,
          os.subCategory,
          os.articleId,
          os.description,
          os.information,
          os.internalType,
          os.shutterType,
          os.remark, os.remarks,
          os.vendorName,
          typeof os.vendor === 'string' ? os.vendor : null,
          os.vendor?.vendorName, os.vendor?.name
        ].some(val => String(val || '').toLowerCase().includes(term));
        const dateMatch = isDateInRange(os.invoiceDate || os.date || os.createdAt, localFilters);
        return searchMatch && dateMatch;
      })
      .map(os => {
        const storeId = typeof os.store === 'object' && os.store !== null ? os.store._id || os.store.id : os.store;
        const catId = typeof os.category === 'object' && os.category !== null ? os.category._id || os.category.id : os.category;
        const unitId = typeof os.unit === 'object' && os.unit !== null ? os.unit._id || os.unit.id : os.unit;

        const storeObj = (dynamicSettings?.stores || []).find(s =>
          String(s?._id) === String(storeId) || String(s?.id) === String(storeId) || String(s) === String(storeId)
        ) || (typeof os.store === 'object' && os.store !== null ? os.store : {});

        const catObj = (dynamicSettings?.cats || []).find(c =>
          String(c?._id) === String(catId) || String(c?.id) === String(catId) || String(c?.categoryId) === String(catId) || String(c) === String(catId)
        ) || (typeof os.category === 'object' && os.category !== null ? os.category : {});

        const unitObj = (dynamicSettings?.units || []).find(u =>
          String(u?._id) === String(unitId) || String(u?.id) === String(unitId) || String(u) === String(unitId)
        ) || (typeof os.unit === 'object' && os.unit !== null ? os.unit : {});

        return {
          ...os,
          name: os.name || os.item?.itemName || os.itemName || 'Unknown Item',
          storeName: storeObj.storeName || storeObj.name || storeObj.title || (typeof os.store === 'string' ? os.store : 'Main Store'),
          categoryDisplay: catObj.categoryName || catObj.title || catObj.name || (typeof os.category === 'string' ? os.category : 'General'),
          unitDisplay: unitObj.unitName || unitObj.title || unitObj.name || (typeof os.unit === 'string' ? os.unit : 'pcs'),
        };
      });
  }, [allOpeningStocks, localSearch, localFilters, dynamicSettings]);
// Excel Export
//updated Halimunnisa 2026-06-08
//Implemented a comprehensive export functionality for closing stock that allows users to easily export their closing stock data to Excel. The export includes robust data mapping and formatting to ensure that the exported file is well-structured and user-friendly, making it easier for users to analyze and share their closing stock information outside of the application. The export process also includes error handling and user feedback to enhance the overall experience when exporting closing stock records.
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const exportStock = allOpenStock || [];

      if (exportStock.length === 0) {
        toast.error("No stock entries to export");
        return;
      }

      const { exportToExcel } = await import("../../../utils/exportUtils");
      
      const exportData = exportStock.map(item => {
        const vendorName = item.vendorName || (typeof item.vendor === 'string' ? item.vendor : null) || (item.vendor && (item.vendor.vendorName || item.vendor.name)) || 'Unknown Vendor';
        
        const itm = item.item || item;
        const getVal = (obj, fields) => {
          if (!obj) return null;
          if (typeof obj === 'string') return obj;
          if (typeof obj === 'object') {
            for (const f of fields) {
              if (obj[f] && typeof obj[f] === 'string') return obj[f];
            }
          }
          return null;
        };
        const safeUnit = getVal(itm.units, ['title', 'name', 'unitName', 'label']) || 
                         getVal(itm.unit, ['title', 'name', 'unitName', 'label']) || 
                         item.uom || '—';

        const qty = Number(item.quantity || item.availableQty || item.stock) || 0;
        const rate = Number(item.blumPrice) || Number(item.vervePrice) || 0;
        const taxable = item.taxableAmount !== undefined ? item.taxableAmount : (qty * rate);
        const gstP = item.gstPercentage !== undefined ? item.gstPercentage : 18;
        const tax = item.taxAmount !== undefined ? item.taxAmount : (taxable * gstP / 100);
        const netAmt = item.netAmount !== undefined ? item.netAmount : (taxable + tax);

        let v = item.version || '—';
        if (typeof v === 'string' && v.toUpperCase().startsWith('V-')) {
          v = v.substring(2);
        }

        // Additional ITEM DETAILS fields
        const blum = Number(item.blumPrice) || 0;
        const verve = Number(item.vervePrice) || 0;
        const priceToUse = blum > 0 ? blum : verve;
        const totalCost = priceToUse * qty;
        
        const minQty = Number(itm.minQty || item.minQty || 0);
        let stockStatus = 'OUT OF STOCK';
        if (qty > minQty) stockStatus = 'IN STOCK';
        else if (qty > 0) stockStatus = 'LOW STOCK';

        return {
          'Vendor Name': vendorName,
          'Category': item.category || '—',
          'Sub Category': item.subCategory || '—',
          'Article ID': item.articleId || '—',
          'Description': item.description || '—',
          'Information': item.information || '—',
          'Internal Type': item.internalType || '—',
          'Shutter Type': item.shutterType || '—',
          'Width': item.width || '—',
          'Height': item.height || '—',
          'Depth': item.depth || '—',
          'UOM': safeUnit,
          'MOQ': item.moq !== undefined ? item.moq : '—',
          'Quantity': qty,
          'Stock Status': stockStatus,
          'Blum Price': item.blumPrice || '—',
          'Verve Price': item.vervePrice || '—',
          'Total Purchases': totalCost,
          'Taxable Amount': taxable,
          'GST %': `${gstP}%`,
          'GST Amount': tax,
          'Net Amount': netAmt,
          'Remarks': item.remark || item.remarks || '—',
          'Date': item.invoiceDate || item.date || item.createdAt ? new Date(item.invoiceDate || item.date || item.createdAt).toLocaleDateString('en-IN') : '—',
          'Version': v
        };
      });

      await exportToExcel(exportData, `Closing_Stock_Export_${new Date().toISOString().split('T')[0]}`);
      toast.success("Closing Stock exported successfully");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export closing stock");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <FilterSection
        search={localSearch}
        setSearch={setLocalSearch}
        filters={localFilters}
        setFilters={handleFilterChange}
        onAddClick={handleNewOpenStock}
        addLabel="Record Closing Stock"
        statusOptions={[]}
        //updated Halimunnisa 2026-06-08
        //Implemented a comprehensive export functionality for closing stock that allows users to easily export their closing stock data to Excel. The export includes robust data mapping and formatting to ensure that the exported file is well-structured and user-friendly, making it easier for users to analyze and share their closing stock information outside of the application. The export process also includes error handling and user feedback to enhance the overall experience when exporting closing stock records.
        onExport={handleExport}
        isExporting={isExporting}
      />
      <OpenStockTable
        items={allOpenStock}
        onView={setViewItem}
        onEdit={handleEditOpenStock}
        onDelete={deleteItem}
      />
    </div>
  );
};
